import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useAppDispatch } from '../../store/store';
import { getMeasurmentPlan, postMeasurmentPlan } from '../../store/slices/vitalSigns';
import { toast } from 'react-toastify';
import { convertMesurmentPlanJSON } from '../../utils/miscUtils';
import TimeSelect from './TimeSelect';
import styles from './MeasurmentTime.module.scss';
import { useTranslation } from '../../hooks/useTranslations';

interface Time {
  hh: string;
  mm: string;
}

export default function MeasurementTimes({
  groupId,
  setScreen,
  personAccountNumber,
}: {
  groupId: number;
  setScreen: Dispatch<SetStateAction<'list' | 'report' | 'measurementTime' | 'manualRecord'>>;
  personAccountNumber?: number;
}) {
  const dispatch = useAppDispatch();
  const [morningTime, setMorningTime] = useState<Time>({ hh: '--', mm: '--' });
  const [bedtimeTime, setBedtimeTime] = useState<Time>({ hh: '--', mm: '--' });
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [reminders, setReminders] = useState<boolean>(true);
  const { translations } = useTranslation();

  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const days = dayKeys.map((dayKey) => ({
    key: dayKey,
    label: translations(dayKey),
  }));

  const handleDayToggle = (dayKey: string) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    );
  };

  const validateFields = () => {
    const newErrors: { days?: string; time?: string } = {};

    if (selectedDays.length === 0) {
      newErrors.days = 'Please select at least one day';
    }

    const hasMorningTime = morningTime.hh !== '--' && morningTime.mm !== '--';
    const hasBedtimeTime = bedtimeTime.hh !== '--' && bedtimeTime.mm !== '--';

    if (!hasMorningTime && !hasBedtimeTime) {
      newErrors.time = 'Please set at least one measurement time (morning or bedtime)';
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // Get user's current timezone offset in format like "+02:00" or "-05:00"
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset(); // Returns offset in minutes, negative for ahead of UTC
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetSign = timezoneOffset <= 0 ? '+' : '-';
    const timezoneString = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

    if (!validateFields()) {
      toast.error(translations('failed_mesurment_plan'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    const measurementPlan: any[] = [];
    selectedDays.forEach((dayKey) => {
      const dayName = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
      if (morningTime.hh !== '--' || morningTime.mm !== '--') {
        measurementPlan.push({
          reminder: reminders,
          time_of_execution: `${morningTime.hh}:${morningTime.mm}:00${timezoneString}`,
          day_of_execution: dayName,
          is_morning: true,
          is_bedtime: false,
        });
      }

      if (bedtimeTime.hh !== '--' || bedtimeTime.mm !== '--') {
        measurementPlan.push({
          reminder: reminders,
          time_of_execution: `${bedtimeTime.hh}:${bedtimeTime.mm}:00${timezoneString}`,
          day_of_execution: dayName,
          is_morning: false,
          is_bedtime: true,
        });
      }
    });

    dispatch(
      postMeasurmentPlan({
        data: { measurement_plan: measurementPlan },
        vital_sign_group_id_out: groupId,
        personId: personAccountNumber || 0,
      })
    )
      .unwrap()
      .then(() => {
        if (measurementPlan.length > 0) {
          toast.success(translations('create_mesurment_plan'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        } else {
          toast.success(translations('reset_mesurment_plan'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      });
  };

  const handleReset = () => {
    setMorningTime({ hh: '--', mm: '--' });
    setBedtimeTime({ hh: '--', mm: '--' });
    setSelectedDays([]);
    setReminders(true);
    dispatch(
      postMeasurmentPlan({
        data: { measurement_plan: [] },
        vital_sign_group_id_out: groupId,
        personId: personAccountNumber || 0,
      })
    );
    toast.success(translations('reset_mesurment_plan'), {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  };

  useEffect(() => {
    if (groupId && personAccountNumber) {
      dispatch(
        getMeasurmentPlan({ vital_sign_group_id_out: groupId, personId: personAccountNumber || 0 })
      )
        .unwrap()
        .then((response: any) => {
          if (response.data.length > 0) {
            const { selectedDays, reminders, morningTime, bedtimeTime } = convertMesurmentPlanJSON(
              response.data
            );
            console.log("morningTime", morningTime)
            setMorningTime(morningTime);
            setBedtimeTime(bedtimeTime);
            setSelectedDays(selectedDays);
            setReminders(reminders);
          }
        });
    }
  }, [groupId, personAccountNumber, dispatch]);

  return (
    <div className={styles.container}>
      <div className={styles.titleWrap}>
        <h3>Measurement times</h3>
        <button className={styles.btn} onClick={handleReset}>
          Reset
        </button>
      </div>
      <div className={styles.timeRow}>
        <div>
          <label>Morning</label>
          <TimeSelect time={morningTime} setTime={setMorningTime} />
        </div>
        <div>
          <label>Bedtime</label>
          <TimeSelect time={bedtimeTime} setTime={setBedtimeTime} />
        </div>
      </div>

      <div className={styles.daysSection}>
        <label>Measurement frequency</label>
        <div className={styles.days}>
          {days.map((day) => (
            <button
              type="button"
              key={day.key}
              onClick={() => handleDayToggle(day.key)}
              className={`${styles.dayBtn} ${selectedDays.includes(day.key) ? styles.active : ''}`}
            >
              {day.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.reminder}>
        <label>Reminders</label>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={reminders}
            onChange={() => setReminders((prev) => !prev)}
          />
          <span className={styles.slider}></span>
        </label>
      </div>
      <div className={styles.btnWrapper}>
        <button className={styles.btn} onClick={handleSubmit}>
          {translations('save')}
        </button>
        <button className={`${styles.btn} ${styles.transparent}`} onClick={() => setScreen('list')}>
          {translations('cancel')}
        </button>
      </div>
    </div>
  );
}
